/**
 * API Service
 * Axios instance with authentication interceptors
 * Automatically adds JWT token and tenant ID to all requests
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import configService from './config';
import authService from '@modules/auth/services/authService';
import { createLogger } from '@/utils/logger';

const logger = createLogger('API');

/**
 * Create Axios instance with base configuration
 */
const api: AxiosInstance = axios.create({
  baseURL: configService.getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - Add authentication headers
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Per-tab session storage: each browser tab can hold a different
    // tenant's auth, so reading from sessionStorage here scopes the
    // request to whichever session belongs to THIS tab.
    const token = sessionStorage.getItem('access_token');
    let tenantId = sessionStorage.getItem('tenant_id');

    // If no separate tenant_id, try to get from user object
    if (!tenantId) {
      try {
        const userStr = sessionStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          tenantId = user.tenant_id;
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }

    // Add Authorization header if token exists
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add X-Tenant-ID header if tenant ID exists
    if (tenantId && config.headers) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    // Log request in development (logger.debug is already a no-op in prod)
    logger.debug(`Request: ${config.method?.toUpperCase()} ${config.url}`, {
      hasToken: !!token,
      hasTenantId: !!tenantId,
    });

    // Remove Content-Type for FormData so axios auto-sets multipart/form-data with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    console.error('[ERROR] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle errors globally
 */
api.interceptors.response.use(
  (response) => {
    // Log successful response in development (logger.debug is already a no-op in prod)
    logger.debug(`Response: ${response.config.url}`, {
      status: response.status,
      data: response.data,
    });
    return response;
  },
  async (error: AxiosError) => {
    // Handle different error types
    if (error.response) {
      const status = error.response.status;
      const url = error.config?.url;

      // The access token is over but this browser remembers this person:
      // renew it and replay the request once, instead of a sign-in screen.
      // A request that left with a token this tab has since replaced (another
      // request's 401 already renewed it) just replays with the current one.
      const original = error.config as (InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean }) | undefined;
      const sentAuthorization = original?.headers?.Authorization;
      const sentWithStaleToken = !!sentAuthorization && sentAuthorization !== `Bearer ${authService.getToken()}`;
      if (status === 401 && original && !original._retriedAfterRefresh && authService.getToken()
          && (sentWithStaleToken || await authService.refreshTabSession())) {
        original._retriedAfterRefresh = true;
        original.headers.Authorization = `Bearer ${authService.getToken()}`;
        return api(original);
      }

      console.error(`[ERROR] API Error [${status}]: ${url}`, error.response.data);

      // Handle 401 Unauthorized - Token expired or invalid.
      // Capture the user's OWN tenant slug BEFORE clearing storage —
      // the post-logout redirect must land on THEIR branded login,
      // never on whichever tenant URL they happen to be viewing.
      if (status === 401) {
        logger.debug('Unauthorized - Clearing auth');
        const u = authService.getUser();
        // The session is already over server-side; drop it in this tab only.
        authService.endTabSession();
        authService.redirectToOwnLogin(u);
      }

      // Handle 403 Forbidden - Insufficient permissions
      if (status === 403) {
        logger.debug('Forbidden - Insufficient permissions');
        // Could show a toast/notification here
      }

      // Handle 404 Not Found
      if (status === 404) {
        logger.debug('Resource not found:', url);
      }

      // Handle 500 Server Error
      if (status >= 500) {
        logger.debug('Server error:', error.response.data);
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('[ERROR] Network error - No response received:', error.message);
    } else {
      // Something happened in setting up the request
      console.error('[ERROR] Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
